import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { getCachedBillingData, getBillingData } from '../data/api';
import parentWindowDataChannel from '../data/ParentWindowDataChannel';
import StripeUpdateCard from './stripe/StripeUpdateCard';
import UpdateCardLoadingState from './stripe/UpdateCardLoadingState';
import UpdateCardErrorState from './stripe/UpdateCardErrorState';
import useStripeLoader, { clearStripeCache } from '../hooks/useStripeLoader';
import { sleep } from '../utils/common';
import {
    ADMIN_NAVIGATION_DESTINATIONS,
    ROUTES,
    WEBHOOK_PROCESSING_DELAY_MS,
} from '../utils/constants';

const DUNNING_TITLE = 'Complete your payment';
const EDIT_TITLE = 'Update your card details.';

// How long the success confirmation stays on the CTA before the page moves on
// to finalizing the update
const PAYMENT_SUCCESS_STATE_MS = 1500;

/**
 * Standalone card page, deep-linkable from Ghost Admin at /update-card. For
 * accounts in dunning it renders the payment form as the page's only content —
 * no billing overview is painted first and covered a beat later. Accounts in
 * good standing get the regular card edit form on the same surface. Accounts
 * whose card isn't the next step (errors, unpaid auto-billing or checkout
 * states, trials) are sent to the Dashboard, which owns those redirects.
 * @param {object} props
 * @param {{push: (route: string, state?: object) => void}} props.history
 * @param {(title: string) => void} props.updateTitle
 * @param {(visible: boolean) => void} props.showNav
 * @param {boolean} [props.returnToAdmin] - Send Admin back after success
 *   to the page the user came from instead of showing the billing overview
 * @returns {React.ReactNode}
 */
function UpdateCard({ history, updateTitle, showNav, returnToAdmin = false }) {
    const [billingData, setBillingData] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const { stripe, error: stripeError, loadStripe } = useStripeLoader({ loadOnMount: true });
    const canClose = !loadingData && !processingPayment && !succeeded;

    const handleClose = useCallback(() => {
        if (canClose) {
            history.push(ROUTES.DASHBOARD);
        }
    }, [canClose, history]);

    useEffect(() => {
        if (!canClose) {
            return;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                event.preventDefault();
                handleClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [canClose, handleClose]);

    useEffect(() => {
        updateTitle('Billing and domain settings');
        showNav(false);

        let cancelled = false;
        const fetchData = async () => {
            const cachedBillingData = await getCachedBillingData();

            if (cancelled) {
                return;
            }

            setBillingData(cachedBillingData);
            setLoadingData(false);
        };
        fetchData();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleRetry = () => {
        clearStripeCache();
        loadStripe();
    };

    /**
     * After a successful payment or card update: leave the confirmation
     * visible for a beat, then move on.
     *
     * On the return route, Ghost Admin is sent back to the page the user
     * came from, and the webhook wait plus the refetch run here in the
     * now-hidden iframe — the refreshed state still reaches the module cache
     * and Ghost Admin (clearing its warnings), just without holding the user
     * on a loading screen. Otherwise the billing overview takes over and
     * holds its regular full-page loader while the webhooks settle.
     */
    const handleSuccess = async () => {
        setSucceeded(true);
        await sleep(PAYMENT_SUCCESS_STATE_MS);

        if (returnToAdmin) {
            parentWindowDataChannel.sendNavigateToAdmin(
                ADMIN_NAVIGATION_DESTINATIONS.PREVIOUS_PAGE,
            );
            await sleep(WEBHOOK_PROCESSING_DELAY_MS);
            await getBillingData({ updateAdmin: true });
            return;
        }

        history.push(ROUTES.DASHBOARD, { finalizePayment: true });
    };

    /**
     * The white surface is portaled to document.body so its fixed positioning
     * always spans the real viewport — untouched by any app wrapper that
     * would otherwise become its containing block or clip it.
     * @param {React.ReactNode} children
     * @param {boolean} [withClose]
     * @returns {React.ReactNode}
     */
    const renderSurface = (children, withClose = false) =>
        createPortal(
            <main className="gh-update-card-page">
                {withClose && (
                    <button
                        className="gh-update-card-page-close"
                        type="button"
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        <svg version="1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M12.707 12L23.854.854a.5.5 0 0 0-.707-.707L12 11.293.854.146a.5.5 0 0 0-.707.707L11.293 12 .146 23.146a.5.5 0 0 0 .708.708L12 12.707l11.146 11.146a.5.5 0 1 0 .708-.706L12.707 12z" />
                        </svg>
                    </button>
                )}
                <div className="gh-update-card-page-content">{children}</div>
            </main>,
            document.body,
        );

    if (loadingData) {
        // The route tells us the intent before the data does: Ghost Admin's
        // dunning warnings deep-link the return variant, while plain visits
        // (the overview's Edit action) must not flash a payment demand at
        // customers in good standing. Either way the flash is brief — the
        // slow-load case is the deep link's fresh document; overview visits
        // resolve from the module cache instantly.
        return renderSurface(
            <UpdateCardLoadingState title={returnToAdmin ? DUNNING_TITLE : EDIT_TITLE} />,
        );
    }

    // Accounts whose card isn't the next step belong on the Dashboard, which
    // owns the follow-up redirects (checkout, plans, error states)
    if (
        billingData?.error ||
        billingData?.isAutoBillingGrace ||
        billingData?.isIncomplete ||
        billingData?.isTrial
    ) {
        return <Redirect to={ROUTES.DASHBOARD} />;
    }

    const isGrace = Boolean(billingData?.isGrace);
    const pageTitle = isGrace ? DUNNING_TITLE : EDIT_TITLE;

    let content;
    if (stripeError) {
        content = <UpdateCardErrorState stripeError={stripeError} onRetry={handleRetry} />;
    } else if (stripe) {
        content = (
            <StripeUpdateCard
                stripe={stripe}
                userId={billingData?.user?.id}
                customerId={
                    billingData?.subscription?.customer_id || billingData?.user?.stripe_token
                }
                isGrace={isGrace}
                outstandingAmount={isGrace ? billingData?.latestInvoice?.amount_due : undefined}
                onCloseModal={handleSuccess}
                onEscape={handleClose}
                setModalLoadingState={setProcessingPayment}
            />
        );
    } else {
        content = (
            <UpdateCardLoadingState title={pageTitle} message="Loading payment processor..." />
        );
    }

    // The close mirrors the old modal's cross; it disappears once a submit is
    // in flight so a stray click can't abandon a payment mid-processing, and
    // stays gone through the paid confirmation's handoff to the overview
    return renderSurface(content, canClose);
}

UpdateCard.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    updateTitle: PropTypes.func.isRequired,
    showNav: PropTypes.func.isRequired,
    returnToAdmin: PropTypes.bool,
};

export default withRouter(UpdateCard);
